/**
 * Calculates the sales tax amount for a line item.
 * @param price - Unit price of the item
 * @param quantity - Quantity of items
 * @param rate - Tax rate as a percentage (e.g., 10 for 10%)
 * @param isTaxExempt - Whether the transaction is tax exempt
 * @returns The calculated tax amount
 */
export const calculateSalesTaxAmount = (
  price: number,
  quantity: number,
  rate: number,
  isTaxExempt: boolean
): number => {
  if (isTaxExempt) {
    return 0;
  }
  return (Number(rate) * Number(price) * Number(quantity)) / 100;
};

/**
 * Calculates the subtotal for a line item (price * quantity).
 * @param price - Unit price of the item
 * @param quantity - Quantity of items
 * @returns The calculated subtotal
 */
export const calculateLineItemSubtotal = (
  price: number,
  quantity: number
): number => {
  return Number(price) * Number(quantity);
};

/**
 * Calculates the subtotal including tax for a line item.
 * @param price - Unit price of the item
 * @param quantity - Quantity of items
 * @param totalTaxRate - Combined tax rate as a percentage
 * @param isTaxExempt - Whether the transaction is tax exempt
 * @returns The calculated subtotal with tax
 */
export const calculateSubtotalWithTax = (
  price: number,
  quantity: number,
  totalTaxRate: number,
  isTaxExempt: boolean
): number => {
  const baseAmount = Number(price) * Number(quantity);
  if (isTaxExempt) {
    return baseAmount;
  }
  return baseAmount * (1 + totalTaxRate / 100);
};

/**
 * Sums up all tax rates from an array of tax objects.
 * @param taxes - Array of tax objects with a rate property
 * @param salesTaxIdKey - The key name for the sales tax ID (to filter valid taxes)
 * @returns The total combined tax rate
 */
export const sumTaxRates = <T extends { rate: number }>(
  taxes: T[],
  filterFn?: (tax: T) => boolean
): number => {
  return taxes.reduce((total, tax) => {
    if (filterFn && !filterFn(tax)) {
      return total;
    }
    return total + Number(tax.rate);
  }, 0);
};
